import React, { useState } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import './list-selection-modal.css';

interface CreateAccountListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (listId: string) => void;
}

export const CreateAccountListModal: React.FC<CreateAccountListModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { createAccountList } = useApi();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('List name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createAccountList(name.trim(), description.trim() || undefined);
      if (result && typeof result === 'object' && result !== null && 'list_id' in result && typeof result.list_id === 'string') {
        onSuccess(result.list_id);
        setName('');
        setDescription('');
        onClose();
      } else {
        setError('Failed to create account list');
      }
    } catch (err) {
      setError('Failed to create account list');
      console.error('Error creating account list:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content list-selection-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Account List</h2>
          <button className="modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="create-list-section">
            <div className="create-list-form">
              <div className="form-group">
                <label htmlFor="name">List Name *</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter list name..."
                  maxLength={200}
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description (Optional)</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter list description..."
                  rows={3}
                  maxLength={500}
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="modal-actions">
            <button
              type="submit"
              disabled={loading || !name.trim()}
              onClick={handleSubmit}
              className="button-primary"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Create List
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="button-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountListModal;
